/**
 * CUSTOMER-R4.15-K9 — cart/checkout merchandising pool without full catalog download.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { loadCategoryPage } from "./catalog-v2-data.js";
import { CROSS_SELL_GLOBAL_FALLBACK } from "./cross-sell-rules.js";
import { merchPoolCategoryIdsForProduct } from "./product-recommendations-v2.js";

const MAX_CATEGORY_SHARDS = 4;

export function merchPoolCategoryIdsForCart(
  products: CatalogV2Product[],
  limit = MAX_CATEGORY_SHARDS,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const product of products) {
    for (const id of merchPoolCategoryIdsForProduct(product, 3)) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      if (out.length >= limit) return out;
    }
  }
  if (out.length === 0) {
    for (const id of CROSS_SELL_GLOBAL_FALLBACK) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      if (out.length >= limit) break;
    }
  }
  return out.slice(0, limit);
}

export async function loadCartRecommendationPool(
  anchors: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  const cats = merchPoolCategoryIdsForCart(anchors, MAX_CATEGORY_SHARDS);
  const pages = await Promise.all(
    cats.map(async (categoryId) => {
      try {
        return await loadCategoryPage(categoryId, 1, "recommended");
      } catch {
        return null;
      }
    }),
  );
  const byId = new Map<string, CatalogV2Product>();
  for (const page of pages) {
    if (!page) continue;
    for (const p of page.products) {
      if (!byId.has(p.id)) byId.set(p.id, p);
    }
  }
  return [...byId.values()];
}
